import { ReactElement, useState } from "react";
import Navbar from "react-bootstrap/Navbar";
import styled from "styled-components";
import { Permission, AuthUser, hasPermission } from "react-ai-platform-auth";
import {
  IconSignOut,
  IconUser,
  Header as SeibunHeader,
  NavigationLinks,
  HeaderUserProfile,
} from "@telusinternationalai/seibun-ui";

import { useAuthContext } from "../../authHelper";
import {
  ACCOUNT,
  ASSESSMENT,
  JOBS,
  LIONBRIDGE_CMP,
  ORGANIZATIONS,
  PROJECTS,
  SUPPORT,
  USERS,
  WALRUS,
} from "../../routes";
import userUtils from "../../utilities/userUtils";
import WarningModal from "../Modals/WarningModal";
import useFeatureFlag from "../../hooks/useFeatureFlag";

const LOGO_LINK = `${process.env.PUBLIC_URL}/img/telus-international-logo.png`;

export const StyledNavbarBrand = styled(Navbar.Brand)`
  && {
    padding: 0.3125rem 2rem 0rem 1.0625rem;
    margin-right: 0;
    &:before {
      display: block;
      content: "";
      background-image: url(${LOGO_LINK});
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      width: 14.539375rem;
      height: 1.5rem;
    }
  }
`;

const StyledSeibunHeader = styled(SeibunHeader)`
  z-index: 100;
  a:hover {
    color: inherit;
    text-decoration: inherit;
  }
`;

type HeaderProps = Readonly<{
  user: AuthUser;
}>;

const Header = ({ user }: HeaderProps): ReactElement => {
  const { logout } = useAuthContext();
  const { fullName = "", userInitial, email } = user;
  const shouldUseCmpContributorSpecificFeatures = userUtils.shouldUseCmpContributorSpecificFeatures(
    user
  );
  const isTiMember = userUtils.belongsToTiOrganization(user);
  const [isLionbridgeLogoutModalOpen, setIsLionbridgeLogoutModalOpen] = useState(false);

  const onLogout = async (): Promise<void> => {
    const isTiLogin = user.isEmployee;
    const disableRedirect = !isTiLogin;
    await logout(disableRedirect);
    if (isTiLogin) {
      setIsLionbridgeLogoutModalOpen(true);
    }
  };

  const isMyWorkEnabled = useFeatureFlag("ENABLE_CMP_MY_WORK");

  const warningModalEl = (
    <WarningModal
      isOpen={isLionbridgeLogoutModalOpen}
      title="Successfully Logged Out of the AI Platform"
      body="You have successfully logged out of the AI Platform. Please note that you may still be logged in to other TELUS International applications on this device."
      onClose={(): void => {
        setIsLionbridgeLogoutModalOpen(false);
        // logoutRedirectUrl is never undefined for a Lionbridge user
        const redirectUrl = user.logoutRedirectUrl as string;
        // TODO: use history
        window.location.replace(redirectUrl);
      }}
      buttonText="OK"
    />
  );

  const links: NavigationLinks = [
    {
      label: "Home",
      path: LIONBRIDGE_CMP.HOME,
      isSamePage: true,
      isHidden: !shouldUseCmpContributorSpecificFeatures,
    },
    hasPermission(user, Permission.READ_PROJECTS)
      ? { label: "Projects", path: PROJECTS.LIST }
      : { label: "Find Jobs", path: LIONBRIDGE_CMP.FIND_JOBS, isSamePage: true },
    {
      label: "Users",
      path: USERS.LIST,
      isHidden: !hasPermission(user, Permission.READ_USERS),
    },
    {
      label: "Organizations",
      path: ORGANIZATIONS.LIST,
      isHidden: !hasPermission(user, Permission.READ_ORGS),
    },
    {
      label: "My Work",
      path: LIONBRIDGE_CMP.MY_WORK_ACTIVE_PROJECTS,
      isSamePage: true,
      isHidden: !isMyWorkEnabled,
    },
    { label: "Qualifications", path: ASSESSMENT.LIST },
    {
      label: "Worksheets",
      path: WALRUS.WORKSHEETS,
      isSamePage: true,
      isHidden: !isTiMember,
    },
    {
      label: "Support",
      path: shouldUseCmpContributorSpecificFeatures ? LIONBRIDGE_CMP.SUPPORT : SUPPORT.FAQ,
      isSamePage: shouldUseCmpContributorSpecificFeatures,
    },
  ];

  return (
    <StyledSeibunHeader
      logoUrl={hasPermission(user, Permission.READ_PROJECTS) ? PROJECTS.LIST : JOBS.LIST}
      links={links}
      isTelusWhite
    >
      <HeaderUserProfile
        className="sui-text-darkGray-darker"
        userDetails={{
          email: email,
          initials: userInitial,
          name: fullName,
        }}
        dropdownList={[
          {
            icon: <IconUser variant="circle" className="sui-h-base sui-w-base sui-mr-2" />,
            isLink: {
              href: shouldUseCmpContributorSpecificFeatures
                ? LIONBRIDGE_CMP.USER_PROFILE
                : ACCOUNT.USER_PROFILE,
            },
            label: "My Profile",
          },
          {
            icon: <IconSignOut className="sui-h-base sui-w-base sui-mr-2" outline />,
            isLink: {
              role: "button",
              onClick: onLogout,
            },
            label: "Sign Out",
          },
        ]}
      />
      {warningModalEl}
    </StyledSeibunHeader>
  );
};

export default Header;
